import { Link } from 'react-router-dom';
import { Minus, Plus, X, Gift, ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';

const CartPage = () => {
  const { cart, loading, updateCartItem, removeFromCart } = useCart();
  const { formatPrice } = useCurrency();

  if (loading) {
    return (
      <div className="pt-32 pb-24 chaleur-container">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-secondary rounded w-48" />
          <div className="h-64 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  if (!cart?.items?.length) {
    return (
      <div className="pt-32 pb-24 chaleur-container" data-testid="cart-page">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
          <h1 className="text-3xl font-light mb-4">Your Cart is Empty</h1>
          <p className="text-muted-foreground mb-8">
            Discover our collection of handcrafted luxury textiles.
          </p>
          <Button asChild className="btn-chaleur-primary">
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </div>
      </div>
    );
  }

  const shippingEstimate = cart.subtotal >= 5000 ? 0 : 199;
  const total = cart.subtotal + shippingEstimate;

  return (
    <div className="pt-32 pb-24" data-testid="cart-page">
      <div className="chaleur-container">
        <h1 className="text-3xl md:text-4xl font-light mb-12">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map(item => (
              <div key={item.id} className="flex gap-6 pb-6 border-b border-border" data-testid={`cart-item-${item.id}`}>
                <Link to={`/product/${item.product_slug}`} className="w-24 h-32 flex-shrink-0 overflow-hidden bg-secondary">
                  <img 
                    src={item.image?.startsWith('/') ? item.image : (item.image || '/images/products/IMG_6195.jpg')}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <Link to={`/product/${item.product_slug}`} className="font-medium hover:text-primary transition-colors">
                        {item.product_name}
                      </Link>
                      <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                      {item.gift_wrap && (
                        <p className="text-xs text-primary mt-1 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> Gift wrapped
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Remove item"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex justify-between items-end mt-4">
                    <div className="flex items-center border border-border">
                      <button 
                        onClick={() => updateCartItem(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-secondary transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-10 text-center text-sm">{item.quantity}</span>
                      <button 
                        onClick={() => updateCartItem(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-secondary transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="font-medium">{formatPrice(item.line_total)}</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Continue Shopping */}
            <Link to="/shop" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors">
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              <span className="link-hover">Continue Shopping</span>
            </Link>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-secondary/30 p-8 sticky top-32">
              <h2 className="text-xl font-medium mb-6">Order Summary</h2>

              {/* Promo Code */}
              <div className="mb-6">
                <label className="text-sm mb-2 block">Promo Code</label>
                <div className="flex gap-2">
                  <Input placeholder="Enter code" className="input-chaleur flex-1" />
                  <Button variant="outline" className="rounded-none">Apply</Button>
                </div>
              </div>

              <div className="space-y-4 py-6 border-y border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatPrice(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{shippingEstimate === 0 ? 'Free' : formatPrice(shippingEstimate)}</span>
                </div>
                {shippingEstimate > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Add {formatPrice(5000 - cart.subtotal)} more for free shipping
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center py-6">
                <span className="font-medium">Total</span>
                <span className="text-xl font-medium">{formatPrice(total)}</span>
              </div>

              <Button asChild className="btn-chaleur-primary w-full" data-testid="checkout-btn">
                <Link to="/checkout">
                  Proceed to Checkout <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-border space-y-2 text-xs text-muted-foreground">
                <p>✓ Secure checkout</p>
                <p>✓ 15-day easy returns</p>
                <p>✓ Authentic craftsmanship guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
